import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Badge,
  Box,
  Button,
  Chip,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import MedicationIcon from '@mui/icons-material/Medication';
import FolderIcon from '@mui/icons-material/Folder';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import VisibilityIcon from '@mui/icons-material/Visibility';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SearchIcon from '@mui/icons-material/Search';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { APP_VERSION } from '../config';
import { usePatient } from '../contexts/PatientContext';
import { useSync } from '../contexts/SyncContext';
import logo from '../assets/logo2025.png';

const drawerWidth = 260;

const actionIcon = (action) => {
  if (action === 'created') return <PersonAddIcon fontSize="small" sx={{ color: 'success.main' }} />;
  if (action === 'deleted') return <DeleteOutlineIcon fontSize="small" sx={{ color: 'error.main' }} />;
  return <EditIcon fontSize="small" sx={{ color: 'warning.main' }} />;
};

const actionColor = (action) => {
  if (action === 'created') return 'success';
  if (action === 'deleted') return 'error';
  return 'warning';
};

const MainLayout = () => {
  const { t, i18n } = useTranslation();
  const { signOut, user } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notifAnchor, setNotifAnchor] = useState(null);
  const { notifications, unreadCount, markAllRead } = useNotifications();
  const { patientId, patientName, activeTab } = usePatient();
  const { isConnected } = useSync();
  const { roleInfo } = useMemo(() => {
    if (!user) return { roleInfo: null };
    const role = user.role || 'read-only';
    const isAdmin = role === 'admin' || role === 'administrator';
    const canWrite = role === 'admin' || role === 'read-write' || role === 'editor';
    const roleName = isAdmin ? t('Administrator') : canWrite ? t('User') : t('Viewer');
    return {
      roleInfo: {
        label: roleName,
        color: isAdmin ? 'error' : canWrite ? 'warning' : 'default'
      }
    };
  }, [user, t]);

  const toggleLanguage = () => {
    const nextLng = i18n.language === 'en' ? 'el' : 'en';
    i18n.changeLanguage(nextLng);
  };

  const navItems = useMemo(
    () => [
      { label: t('Dashboard'), icon: <DashboardIcon />, path: '/dashboard' },
      { label: t('Patients'), icon: <PeopleIcon />, path: '/patients' },
      { label: t('Query'), icon: <SearchIcon />, path: '/query' },
      { label: t('Settings'), icon: <SettingsIcon />, path: '/settings' },
      { label: t('Help Manual'), icon: <HelpOutlineIcon />, path: '/help' },
    ],
    [t]
  );

  const handleDrawerToggle = () => setMobileOpen((prev) => !prev);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  const handleNotifOpen = (e) => {
    setNotifAnchor(e.currentTarget);
    if (unreadCount > 0) markAllRead();
  };
  const handleNotifClose = () => setNotifAnchor(null);
  const notifOpen = Boolean(notifAnchor);

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Brand header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          minHeight: 64,
        }}
      >
        <Box
          component="img"
          src={logo}
          alt="MyEMR Logo"
          sx={{ width: 56, height: 56, objectFit: 'contain', borderRadius: 1 }}
        />
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2, fontSize: '1rem' }}>
            Bolanis Ioannis 2026
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2, fontSize: '1rem', color: 'primary.main', display: 'flex', alignItems: 'center' }}>
            MyEMR
            <Typography component="span" sx={{ fontSize: '1rem', fontWeight: 500, color: 'text.secondary', ml: 0.8, opacity: 0.8 }}>
              v{APP_VERSION}
            </Typography>
          </Typography>
        </Box>
      </Box>

      {/* Nav items */}
      <List sx={{ flex: 1, pt: 1 }}>
        {navItems.map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton
              component={NavLink}
              to={item.path}
              onClick={() => setMobileOpen(false)}
              sx={{
                mx: 1,
                borderRadius: 2,
                mb: 0.5,
                '&.active': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  '& .MuiListItemIcon-root': { color: 'primary.contrastText' },
                },
                '&:not(.active):hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
              <ListItemText
                primary={t(item.label)}
                primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      {patientId && (
        <>
          <Divider />
          <Box sx={{ px: 2, pt: 2, pb: 1 }}>
            <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 1.2 }}>
              {t('Active Patient')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, mb: 1 }}>
              <AccountCircleIcon fontSize="small" color="primary" />
              <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {patientName}
              </Typography>
            </Box>
          </Box>
          <List sx={{ pt: 0 }}>
            {[
              { label: t('Medical History'), icon: <AssignmentIcon />, tab: 0 },
              { label: t('Visits'), icon: <CalendarMonthIcon />, tab: 1 },
              { label: t('Operations'), icon: <MedicalServicesIcon />, tab: 2 },
              { label: t('Medications'), icon: <MedicationIcon />, tab: 3 },
              { label: t('Patient Files'), icon: <FolderIcon />, tab: 4 },
              { label: t('Gallery'), icon: <VisibilityIcon />, tab: 5 },
            ].map((item) => (
              <ListItem key={item.tab} disablePadding>
                <ListItemButton
                  component={NavLink}
                  to={`/patients/${patientId}?tab=${item.tab}`}
                  onClick={() => setMobileOpen(false)}
                  sx={{
                    mx: 1,
                    borderRadius: 2,
                    mb: 0.5,
                    backgroundColor: activeTab === item.tab ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                    borderLeft: activeTab === item.tab ? '3px solid' : 'none',
                    borderColor: 'primary.main',
                    pl: activeTab === item.tab ? '13px' : '16px',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                    '& .MuiListItemIcon-root': {
                      color: activeTab === item.tab ? 'primary.main' : 'text.secondary',
                      minWidth: 36,
                    },
                    '& .MuiListItemText-root': {
                      color: activeTab === item.tab ? 'primary.main' : 'text.primary',
                    },
                  }}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ fontWeight: 600, fontSize: '0.85rem' }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </>
      )}

      <Divider />
      {/* Sign out */}
      <List sx={{ pb: 1 }}>
        <ListItem disablePadding>
          <ListItemButton onClick={handleSignOut} sx={{ mx: 1, borderRadius: 2 }}>
            <ListItemIcon sx={{ minWidth: 36 }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText
              primary={t('Logout')}
              primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem' }}
            />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          color: 'text.primary',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
            <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', gap: 1.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.875rem' }}>
                {t('Logged in as')}:
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.925rem' }}>
                {user?.name || user?.username || user?.email || t('Unknown User')}
              </Typography>
              {roleInfo && (
                <Chip
                  label={roleInfo.label}
                  color={roleInfo.color}
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase' }}
                />
              )}
            </Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'primary.main', display: { xs: 'block', sm: 'none' } }}>
              MyEMR
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} alignItems="center">
            {/* Clinical Link Status */}
            <Tooltip title={isConnected ? t('Clinical Link Active') : t('Clinical Link Disconnected')}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                px: 1.5, 
                py: 0.5, 
                borderRadius: 2, 
                bgcolor: isConnected ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)', 
                border: '1px solid', 
                borderColor: isConnected ? 'success.main' : 'error.main', 
                transition: 'all 0.3s ease',
                cursor: 'default'
              }}>
                <Box 
                  sx={{ 
                    width: 8, 
                    height: 8, 
                    borderRadius: '50%', 
                    bgcolor: isConnected ? 'success.main' : 'error.main',
                    mr: isConnected ? 1 : 0,
                    boxShadow: isConnected ? '0 0 8px #4caf50' : 'none',
                    animation: isConnected ? 'pulse-status 2s infinite' : 'none',
                    '@keyframes pulse-status': {
                      '0%': { transform: 'scale(0.95)', boxShadow: '0 0 0 0 rgba(76, 175, 80, 0.7)' },
                      '70%': { transform: 'scale(1)', boxShadow: '0 0 0 6px rgba(76, 175, 80, 0)' },
                      '100%': { transform: 'scale(0.95)', boxShadow: '0 0 0 0 rgba(76, 175, 80, 0)' },
                    }
                  }} 
                />
                {isConnected && (
                  <Typography variant="caption" sx={{ fontWeight: 800, color: 'success.main', display: { xs: 'none', md: 'block' }, letterSpacing: 0.5 }}>
                    {t('HUB LINK')}
                  </Typography>
                )}
              </Box>
            </Tooltip>

            {/* Language Switcher */}
            <Tooltip title={i18n.language === 'en' ? t('Switch to Greek') : t('Switch to English')}>
              <Button
                onClick={toggleLanguage}
                variant="outlined"
                color="inherit"
                size="small"
                sx={{
                  borderRadius: '12px',
                  minWidth: '54px',
                  height: '40px',
                  px: 1,
                  fontWeight: 700,
                  fontSize: '1.4rem',
                  borderColor: 'rgba(255,255,255,0.3)',
                  color: 'inherit',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.5)' }
                }}
              >
                {i18n.language === 'en' ? '🇺🇸' : '🇬🇷'}
              </Button>
            </Tooltip>
            {/* Notifications */}
            <Tooltip title={t('Notifications')}>
              <IconButton color="inherit" onClick={handleNotifOpen}>
                <Badge badgeContent={unreadCount} color="error" max={99}>
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            </Tooltip>

            <Tooltip title={t('Logout')}>
              <IconButton color="inherit" onClick={handleSignOut} size="small">
                <LogoutIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Toolbar>
      </AppBar>

      {/* Notification popover */}
      <Popover
        open={notifOpen}
        anchorEl={notifAnchor}
        onClose={handleNotifClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { width: 380, maxHeight: 480, overflow: 'hidden', display: 'flex', flexDirection: 'column' } }}
      >
        <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle1" fontWeight={700}>Notifications</Typography>
          {notifications.length > 0 && (
            <Button size="small" startIcon={<CheckCircleOutlineIcon />} onClick={markAllRead}>
              Mark all read
            </Button>
          )}
        </Box>
        <Box sx={{ overflowY: 'auto', flex: 1 }}>
          {notifications.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <NotificationsIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">No notifications yet</Typography>
            </Box>
          ) : (
            notifications.map((n) => (
              <Box
                key={n.id}
                sx={{
                  px: 2,
                  py: 1.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  gap: 1.5,
                  alignItems: 'flex-start',
                  bgcolor: n.read ? 'transparent' : 'action.hover',
                }}
              >
                <Box sx={{ mt: 0.3 }}>{actionIcon(n.action)}</Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: n.read ? 400 : 600, lineHeight: 1.4 }}>
                    {n.message}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <Chip
                      label={n.action || 'change'}
                      size="small"
                      color={actionColor(n.action)}
                      variant="outlined"
                      sx={{ height: 18, fontSize: '0.65rem' }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {new Date(n.created_at).toLocaleString()}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            ))
          )}
        </Box>
      </Popover>

      {/* Sidebar drawer */}
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="navigation"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1.5, sm: 2, md: 3 },
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          backgroundColor: 'background.default',
          overflowX: 'hidden'
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>
    </Box>
  );
};

export default MainLayout;
